import React, { useMemo, useState } from 'react';
import { Card, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import './PaymentCalendar.scss';

dayjs.extend(isBetween);

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const STATUS_CONFIG = {
  overdue:   { color: '#ff4d4f', label: 'Vencido'   },
  upcoming:  { color: '#faad14', label: 'Próximo'   },
  paid:      { color: '#52c41a', label: 'Pagado'    },
  pending:   { color: '#bfbfbf', label: 'Pendiente' },
};

function buildCalendarGrid(year, month) {
  const first = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
  const startDow = first.day();
  const daysInMonth = first.daysInMonth();
  const prevMonthDays = first.subtract(1, 'month').daysInMonth();

  const cells = [];
  for (let i = startDow - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, current: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, current: false });
  }

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

const PaymentCalendar = ({ allExpenses = [], onDateClick, loading = false }) => {
  const today = dayjs();
  const [month, setMonth] = useState(today.month() + 1);
  const [year,  setYear]  = useState(today.year());

  const expensesByDate = useMemo(() => {
    const map = {};
    allExpenses.forEach((expense) => {
      if (!expense.date || !dayjs(expense.date).isValid()) return;
      const dateStr = dayjs(expense.date).format('YYYY-MM-DD');
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(expense);
    });
    return map;
  }, [allExpenses]);

  const getDateStatus = (dateStr, expenses) => {
    if (!expenses || expenses.length === 0) return null;
    const todayObj = dayjs().startOf('day');
    const dateObj  = dayjs(dateStr).startOf('day');

    const hasPending = expenses.some(exp => {
      const s = String(exp.status || '').toLowerCase();
      return s === 'pendiente' || s === 'pending';
    });
    const hasPaid = expenses.some(exp => {
      const s = String(exp.status || '').toLowerCase();
      return s === 'pagado' || s === 'paid';
    });

    if (hasPending) {
      if (dateObj.isBefore(todayObj)) return 'overdue';
      if (dateObj.isBetween(todayObj, todayObj.add(7, 'day'), null, '[]')) return 'upcoming';
      return 'pending';
    }
    if (hasPaid) return 'paid';
    return null;
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleDayClick = (day, isCurrent) => {
    if (!isCurrent || !onDateClick) return;
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const payments = expensesByDate[dateStr];
    if (!payments || payments.length === 0) return;
    onDateClick(dateStr, payments);
  };

  const weeks    = buildCalendarGrid(year, month);
  const todayStr = today.format('YYYY-MM-DD');

  return (
    <Card
      className="pcal-card"
      title={<span style={{ fontSize: 14, fontWeight: 700 }}>Calendario de Pagos</span>}
      bodyStyle={{ padding: '12px 16px' }}
    >
      {/* Leyenda */}
      <div className="pcal-legend">
        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
          <span key={k} className="pcal-legend-item">
            <span className="pcal-dot" style={{ background: v.color }} />
            {v.label}
          </span>
        ))}
      </div>

      {/* Navegación mes */}
      <div className="pcal-nav">
        <button className="pcal-nav-btn" onClick={prevMonth}><LeftOutlined /></button>
        <span className="pcal-nav-title">{MONTHS_ES[month - 1]} {year}</span>
        <button className="pcal-nav-btn" onClick={nextMonth}><RightOutlined /></button>
      </div>

      {/* Grid */}
      <Spin spinning={loading}>
        <div className="pcal-grid">
          {DAYS_ES.map(d => (
            <div key={d} className="pcal-dow">{d}</div>
          ))}

          {weeks.flat().map((cell, idx) => {
            const dateStr  = cell.current
              ? `${year}-${String(month).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`
              : null;
            const expenses = dateStr ? expensesByDate[dateStr] : null;
            const status   = dateStr ? getDateStatus(dateStr, expenses) : null;
            const isToday  = dateStr === todayStr;
            const hasEvent = !!expenses && expenses.length > 0;
            const cfg      = status ? STATUS_CONFIG[status] : null;

            return (
              <div
                key={idx}
                className={[
                  'pcal-cell',
                  !cell.current ? 'pcal-cell--other'     : '',
                  isToday       ? 'pcal-cell--today'     : '',
                  hasEvent      ? 'pcal-cell--has-event' : '',
                ].join(' ')}
                onClick={() => handleDayClick(cell.day, cell.current)}
                title={cfg && hasEvent ? `${cfg.label} · ${expenses.length} gasto(s)` : undefined}
              >
                <span className="pcal-day-num">{cell.day}</span>
                {cfg && (
                  <span className="pcal-event-dot" style={{ background: cfg.color }} />
                )}
              </div>
            );
          })}
        </div>
      </Spin>
    </Card>
  );
};

export default PaymentCalendar;
